import * as lib from '~/dependencies';

/**
 * Returns media devices grouped by unique deviceId and kind (audioinput, audiooutput, video)
 * @param {Array} devices - An array of devices got from mediaDevices.enumerateDevices()
 * @param {String} kind - A string e.g: audioinput, audiooutput or video
 */
export function getMidiaDevicesByGroupId(devices, kind) {
  if (
    !lib._.isObject(devices) ||
    lib._.isEmpty(devices) ||
    lib._.isEmpty(kind)
  ) {
    return [];
  }

  const uniqueDevices = Array.from(new Set(devices.map(d => d.groupId))).map(
    groupId => {
      return devices.find(d => d.groupId === groupId && d.kind === kind);
    }
  );

  // Keep default devices on the first position
  const devicesSorted = uniqueDevices.sort(o => {
    if (o.deviceId === 'default') {
      return -1;
    }
    return 0;
  });

  // Remove undefined deviceIds
  return devicesSorted.filter(e => e && e.deviceId);
}

/**
 * @name sdpModifierCodec
 * @description Change the SDP to always negotiate Opus and G.711 respectively.
 * @param {string} description
 * @returns Promise<string>
 */
export function sdpModifierCodec(description) {
  description.sdp = description.sdp.replace(
    'UDP/TLS/RTP/SAVPF 111 63 103 104 9 0 8 106 105 13 110 112 113 126',
    'RTP/SAVPF 111 8 103 104 9 0 63 106 105 13 110 112 113 126'
  );

  return Promise.resolve(description);
}

/**
 * @name sdpModifierCodec
 * @description Store Media Stream Globally on Window
 * { remoteStream, localStream,mediaStreamSession1, mediaStreamSession2, mediaStreamSession1Recv, mixer }
 * @param {object [MediaStream]} mediaStreams
 * @returns void
 */
function setMediaStreams(mediaStreams) {
  const { globalMediaStreams } = window;

  if (globalMediaStreams) {
    globalMediaStreams.push(mediaStreams);
  } else {
    window.globalMediaStreams = [];
    window.globalMediaStreams.push(mediaStreams);
  }
}

function clearMediaStreams() {
  const { globalMediaStreams } = window;

  globalMediaStreams?.forEach((media, index) => {
    media.remoteStream?.getTracks().forEach(track => track.stop());
    media.localStream?.getTracks().forEach(track => track.stop());
    media?.mediaStreamSession1?.getTracks().forEach(track => track.stop());
    media?.mediaStreamSession2?.getTracks().forEach(track => track.stop());
    media?.mediaStreamSession1Recv?.getTracks().forEach(track => track.stop());
    media?.mixer?.getTracks().forEach(track => track.stop());

    if (index === globalMediaStreams.length - 1) {
      window.globalMediaStreams = [];
    }
  });
}
function getActiveMediaStreams() {
  return window.globalMediaStreams;
}

/**
 * Check for at least one video device
 */
export async function checkVideoDevice() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  const hasVideoDevice = devices.find(e => e.kind === 'videoinput');

  if (hasVideoDevice) {
    return true;
  }

  return false;
}

export const mediaStreamMonitor = {
  setMediaStreams,
  clearMediaStreams,
  getActiveMediaStreams,
  checkVideoDevice,
};
